import React, { Component, type ReactNode } from 'react';
import { FeatureLoadError } from './FeatureLoadError';
import { isChunkLoadError, reloadOnceForChunkError } from '../utils/chunkLoadError';
import { markProgrammaticReload } from '../../../services/analytics/pageLoad';

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (reloadOnceForChunkError(error)) return;
    console.error('Route error boundary caught:', error, info.componentStack);
  }

  private handleRetry = (): void => {
    markProgrammaticReload('route_error_retry');
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    const message = isChunkLoadError(error)
      ? 'We could not load this part of the app. This is usually a temporary network issue or a new version being released.'
      : 'Something went wrong while loading this page.';

    return <FeatureLoadError error={message} onRetry={this.handleRetry} />;
  }
}
